import React, { useState, useEffect, useRef, useCallback } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { toast } from 'sonner';
import {
    TrendingUp, ShoppingCart, Package, Users, ArrowUpRight, ArrowDownRight,
    Factory, Layers, RefreshCw, Download, ChevronDown, Calendar
} from 'lucide-react';
import api from '../services/api';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const fmtDec = (v) => (v || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtInt = (v) => (v || 0).toLocaleString('de-DE');

const PERIODS = [
    { key: 'today', label: 'Bugun' },
    { key: 'week', label: 'Bu Hafta' },
    { key: 'month', label: 'Bu Ay' },
    { key: 'all', label: 'Tum Zamanlar' },
];

export default function Reports() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [period, setPeriod] = useState('month');
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [pdfLoading, setPdfLoading] = useState(false);
    const reportRef = useRef(null);
    const dropdownRef = useRef(null);

    useEffect(() => { fetchData(); }, []);

    // Close dropdown on outside click
    useEffect(() => {
        const handler = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setDropdownOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try { setData((await api.get('/reports/summary')).data); }
        catch { toast.error('Rapor verileri yuklenemedi'); }
        finally { setLoading(false); }
    };

    const handlePdfDownload = useCallback(async () => {
        if (!reportRef.current) return;
        setPdfLoading(true);
        try {
            const canvas = await html2canvas(reportRef.current, {
                scale: 2, useCORS: true, backgroundColor: '#F9FAFB',
                logging: false, windowWidth: 1200,
            });
            const imgData = canvas.toDataURL('image/jpeg', 0.95);
            const pdf = new jsPDF('p', 'mm', 'a4');
            const pdfW = pdf.internal.pageSize.getWidth();
            const pdfH = (canvas.height * pdfW) / canvas.width;
            const pageH = pdf.internal.pageSize.getHeight();
            let y = 0;
            while (y < pdfH) {
                if (y > 0) pdf.addPage();
                pdf.addImage(imgData, 'JPEG', 0, -y, pdfW, pdfH);
                y += pageH;
            }
            const periodLabel = PERIODS.find(p => p.key === period)?.label || '';
            pdf.save(`Flowbil_Rapor_${periodLabel}_${new Date().toISOString().slice(0, 10)}.pdf`);
            toast.success('PDF indirildi');
        } catch {
            toast.error('PDF olusturulamadi');
        } finally {
            setPdfLoading(false);
        }
    }, [period]);

    if (loading) return (
        <DashboardLayout>
            <div className="space-y-4">
                <h1 className="page-title">Raporlar</h1>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">{[1,2,3,4].map(i => <div key={i} className="h-24 bg-[#F3F4F6] rounded-2xl animate-pulse" />)}</div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">{[1,2].map(i => <div key={i} className="h-64 bg-[#F3F4F6] rounded-2xl animate-pulse" />)}</div>
            </div>
        </DashboardLayout>
    );

    if (!data) return <DashboardLayout><div className="text-center py-16 text-[#86868B]">Veri bulunamadi</div></DashboardLayout>;

    const { orders, top_products, top_customers, monthly, stock, movements, raw_materials } = data;

    // Period-based values
    const periodMap = {
        today:  { revenue: orders.today_revenue,  count: orders.today_count,  production: movements.production_today,   label: 'Bugun' },
        week:   { revenue: orders.week_revenue,   count: orders.week_count,   production: movements.production_this_week, label: 'Bu Hafta' },
        month:  { revenue: orders.month_revenue,  count: orders.month_count,  production: movements.production_this_month, label: 'Bu Ay' },
        all:    { revenue: orders.total_revenue,   count: orders.total_count,  production: movements.total_production,    label: 'Toplam' },
    };
    const pv = periodMap[period];
    const avgOrder = pv.count > 0 ? pv.revenue / pv.count : 0;

    const maxProdQty = top_products.length > 0 ? Math.max(...top_products.map(p => p.quantity)) : 1;
    const maxCustTotal = top_customers.length > 0 ? Math.max(...top_customers.map(c => c.total)) : 1;
    const maxMonthly = monthly.length > 0 ? Math.max(...monthly.map(m => m.total)) : 1;
    const currentPeriodLabel = PERIODS.find(p => p.key === period)?.label;

    return (
        <DashboardLayout>
            <div className="space-y-5" data-testid="reports-page">
                {/* Header */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                    <h1 className="page-title">Raporlar</h1>
                    <div className="flex items-center gap-2">
                        {/* Period Dropdown */}
                        <div className="relative" ref={dropdownRef}>
                            <button
                                onClick={() => setDropdownOpen(!dropdownOpen)}
                                className="flex items-center gap-2 px-3.5 py-2 bg-white border border-[#E5E7EB] rounded-xl text-sm font-medium text-[#1D1D1F] hover:border-[#007AFF] transition-colors"
                                data-testid="period-dropdown-btn"
                            >
                                <Calendar size={14} className="text-[#86868B]" />
                                {currentPeriodLabel}
                                <ChevronDown size={14} className={`text-[#86868B] transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
                            </button>
                            {dropdownOpen && (
                                <div className="absolute right-0 top-full mt-1 bg-white border border-[#E5E7EB] rounded-xl shadow-lg py-1 z-50 min-w-[160px]" data-testid="period-dropdown-menu">
                                    {PERIODS.map(p => (
                                        <button
                                            key={p.key}
                                            onClick={() => { setPeriod(p.key); setDropdownOpen(false); }}
                                            className={`w-full text-left px-4 py-2 text-sm transition-colors ${period === p.key ? 'bg-[#007AFF]/10 text-[#007AFF] font-medium' : 'text-[#1D1D1F] hover:bg-[#F3F4F6]'}`}
                                            data-testid={`period-option-${p.key}`}
                                        >
                                            {p.label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        {/* PDF Download */}
                        <button
                            onClick={handlePdfDownload}
                            disabled={pdfLoading}
                            className="flex items-center gap-2 px-3.5 py-2 bg-[#1D1D1F] text-white rounded-xl text-sm font-medium hover:bg-[#333] transition-colors disabled:opacity-50"
                            data-testid="download-pdf-btn"
                        >
                            <Download size={14} />
                            {pdfLoading ? 'Hazirlaniyor...' : 'PDF Indir'}
                        </button>
                        {/* Refresh */}
                        <button onClick={fetchData} className="flex items-center gap-2 px-3 py-2 bg-white border border-[#E5E7EB] rounded-xl text-sm text-[#6B7280] hover:border-[#007AFF] transition-colors" data-testid="refresh-reports-btn">
                            <RefreshCw size={14} />
                        </button>
                    </div>
                </div>

                {/* Report Content (for PDF capture) */}
                <div ref={reportRef} className="space-y-5">
                    {/* KPI Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <KpiCard icon={ShoppingCart} label={`Satis (${pv.label})`} value={`${fmtDec(pv.revenue)} EUR`} sub={`${pv.count} siparis`} color="text-[#007AFF]" bgColor="bg-[#007AFF]/10" />
                        <KpiCard icon={Factory} label={`Uretim (${pv.label})`} value={fmtInt(pv.production)} sub="adet" color="text-purple-600" bgColor="bg-purple-50" />
                        <KpiCard icon={TrendingUp} label="Ort. Siparis" value={`${fmtDec(avgOrder)} EUR`} sub={`${pv.count} siparis`} color="text-emerald-600" bgColor="bg-emerald-50" />
                        <KpiCard icon={Package} label="Stokta" value={fmtInt(stock.total_items)} sub={`${stock.product_count} urun`} color="text-amber-600" bgColor="bg-amber-50" />
                    </div>

                    {/* Secondary KPIs - All time quick stats */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
                        <MiniKpi label="Bugunku Satis" value={`${fmtDec(orders.today_revenue)} EUR`} sub={`${orders.today_count} sip.`} />
                        <MiniKpi label="Haftalik Satis" value={`${fmtDec(orders.week_revenue)} EUR`} sub={`${orders.week_count} sip.`} />
                        <MiniKpi label="Aylik Satis" value={`${fmtDec(orders.month_revenue)} EUR`} sub={`${orders.month_count} sip.`} />
                        <MiniKpi label="Stok Giris" value={fmtInt(movements.total_in)} icon={<ArrowDownRight size={12} className="text-emerald-500" />} />
                        <MiniKpi label="Stok Cikis" value={fmtInt(movements.total_out)} icon={<ArrowUpRight size={12} className="text-red-400" />} />
                        <MiniKpi label="Hammadde" value={`${raw_materials.in_stock_count} stokta`} sub={`${(raw_materials.in_stock_kg || 0).toLocaleString('de-DE', {minimumFractionDigits:0})} kg`} />
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {/* Top Products */}
                        <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5" data-testid="top-products-report">
                            <h2 className="text-sm font-semibold text-[#1D1D1F] mb-4">En Cok Satan Urunler</h2>
                            {top_products.length > 0 ? (
                                <div className="space-y-2.5">
                                    {top_products.map((p, i) => (
                                        <div key={i} className="flex items-center gap-3">
                                            <span className="text-xs text-[#86868B] w-5 text-right">{i + 1}</span>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-baseline justify-between mb-1">
                                                    <span className="text-sm font-medium truncate">{p.product_name}{p.variant_name ? ` · ${p.variant_name}` : ''}</span>
                                                    <span className="text-sm font-bold ml-2 flex-shrink-0">{fmtInt(p.quantity)} ad</span>
                                                </div>
                                                <div className="h-1.5 bg-[#F3F4F6] rounded-full overflow-hidden">
                                                    <div className="h-full bg-[#007AFF] rounded-full transition-all" style={{ width: `${(p.quantity / maxProdQty) * 100}%` }} />
                                                </div>
                                            </div>
                                            <span className="text-xs text-[#86868B] w-20 text-right">{fmtDec(p.revenue)} EUR</span>
                                        </div>
                                    ))}
                                </div>
                            ) : <p className="text-sm text-[#86868B]">Henuz satis verisi yok</p>}
                        </div>

                        {/* Top Customers */}
                        <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5" data-testid="top-customers-report">
                            <h2 className="text-sm font-semibold text-[#1D1D1F] mb-4">En Cok Siparis Veren Musteriler</h2>
                            {top_customers.length > 0 ? (
                                <div className="space-y-2.5">
                                    {top_customers.map((c, i) => (
                                        <div key={i} className="flex items-center gap-3">
                                            <span className="text-xs text-[#86868B] w-5 text-right">{i + 1}</span>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-baseline justify-between mb-1">
                                                    <span className="text-sm font-medium truncate">{c.customer_name}</span>
                                                    <span className="text-sm font-bold ml-2 flex-shrink-0">{fmtDec(c.total)} EUR</span>
                                                </div>
                                                <div className="h-1.5 bg-[#F3F4F6] rounded-full overflow-hidden">
                                                    <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${(c.total / maxCustTotal) * 100}%` }} />
                                                </div>
                                            </div>
                                            <span className="text-xs text-[#86868B] w-14 text-right">{c.order_count} sip.</span>
                                        </div>
                                    ))}
                                </div>
                            ) : <p className="text-sm text-[#86868B]">Henuz musteri verisi yok</p>}
                        </div>
                    </div>

                    {/* Monthly Chart */}
                    <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5" data-testid="monthly-report">
                        <h2 className="text-sm font-semibold text-[#1D1D1F] mb-4">Aylik Satis Grafigi</h2>
                        {monthly.length > 0 ? (
                            <div className="flex items-end gap-3 h-40">
                                {monthly.map((m, i) => (
                                    <div key={i} className="flex-1 flex flex-col items-center justify-end h-full">
                                        <span className="text-xs font-bold text-[#1D1D1F] mb-1">{fmtDec(m.total)}</span>
                                        <div className="w-full bg-[#007AFF]/80 rounded-t-lg transition-all hover:bg-[#007AFF]"
                                            style={{ height: `${Math.max((m.total / maxMonthly) * 100, 4)}%` }}
                                            title={`${m.count} siparis`} />
                                        <span className="text-[10px] text-[#86868B] mt-2 font-medium">{m.month.slice(5)}/{m.month.slice(2, 4)}</span>
                                        <span className="text-[10px] text-[#9CA3AF]">{m.count} sip.</span>
                                    </div>
                                ))}
                            </div>
                        ) : <p className="text-sm text-[#86868B]">Henuz aylik veri yok</p>}
                    </div>

                    {/* Production & Movements Detail */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5" data-testid="movements-report">
                            <h2 className="text-sm font-semibold text-[#1D1D1F] mb-3">Stok Hareketleri</h2>
                            <div className="space-y-3">
                                <StatRow label="Toplam Giris" value={fmtInt(movements.total_in)} color="text-emerald-600" />
                                <StatRow label="Toplam Cikis" value={fmtInt(movements.total_out)} color="text-red-500" />
                                <div className="border-t border-[#F3F4F6] pt-3 mt-3">
                                    <p className="text-xs font-semibold text-[#86868B] uppercase tracking-wide mb-2">Uretim</p>
                                    <div className="space-y-2">
                                        <StatRow label="Bugun" value={fmtInt(movements.production_today)} color="text-[#007AFF]" />
                                        <StatRow label="Bu Hafta" value={fmtInt(movements.production_this_week)} color="text-indigo-600" />
                                        <StatRow label="Bu Ay" value={fmtInt(movements.production_this_month)} color="text-purple-600" />
                                        <StatRow label="Toplam" value={fmtInt(movements.total_production)} color="text-amber-600" />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5" data-testid="raw-materials-report">
                            <h2 className="text-sm font-semibold text-[#1D1D1F] mb-3">Hammadde Durumu</h2>
                            <div className="space-y-3">
                                <StatRow label="Stokta" value={`${raw_materials.in_stock_count} adet`} color="text-emerald-600" />
                                <StatRow label="Stokta Agirlik" value={`${(raw_materials.in_stock_kg || 0).toLocaleString('de-DE', {minimumFractionDigits:1})} kg`} color="text-blue-600" />
                                <StatRow label="Kullanildi" value={`${raw_materials.used_count} adet`} color="text-gray-500" />
                                <StatRow label="Kullanilan Agirlik" value={`${(raw_materials.used_kg || 0).toLocaleString('de-DE', {minimumFractionDigits:1})} kg`} color="text-gray-400" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </DashboardLayout>
    );
}

function KpiCard({ icon: Icon, label, value, sub, color, bgColor }) {
    return (
        <div className="bg-white rounded-2xl border border-[#E5E7EB] p-4">
            <div className="flex items-center gap-2 mb-2">
                <div className={`w-8 h-8 rounded-xl ${bgColor} flex items-center justify-center`}><Icon size={16} className={color} /></div>
                <span className="text-xs text-[#86868B]">{label}</span>
            </div>
            <p className={`text-lg font-bold ${color}`}>{value}</p>
            {sub && <p className="text-[11px] text-[#9CA3AF]">{sub}</p>}
        </div>
    );
}

function MiniKpi({ label, value, sub, icon }) {
    return (
        <div className="bg-white rounded-2xl border border-[#E5E7EB] p-3">
            <p className="text-[11px] text-[#86868B] mb-0.5">{label}</p>
            <p className="text-sm font-bold text-[#1D1D1F] flex items-center gap-1">{icon}{value}</p>
            {sub && <p className="text-[10px] text-[#9CA3AF]">{sub}</p>}
        </div>
    );
}

function StatRow({ label, value, color }) {
    return (
        <div className="flex items-center justify-between">
            <span className="text-sm text-[#6B7280]">{label}</span>
            <span className={`text-sm font-semibold ${color}`}>{value}</span>
        </div>
    );
}
